import { useEffect, useMemo, useState } from 'react'
import { getProductStockMap, isProductInStock, type ProductStockMap } from '#bv/lib/productStock'

let cachedStockMap: ProductStockMap | null = null
let stockRequest: Promise<ProductStockMap> | null = null

async function loadStockMap(force = false): Promise<ProductStockMap> {
  if (!force && cachedStockMap) return cachedStockMap
  if (!stockRequest) {
    stockRequest = getProductStockMap()
      .then((map) => {
        cachedStockMap = map
        return map
      })
      .finally(() => {
        stockRequest = null
      })
  }
  return stockRequest
}

export function useProductStock() {
  const [stockMap, setStockMap] = useState<ProductStockMap>(cachedStockMap ?? {})
  const [loaded, setLoaded] = useState<boolean>(!!cachedStockMap)

  useEffect(() => {
    let active = true
    const refresh = (force = false) =>
      loadStockMap(force)
        .then((map) => {
          if (!active) return
          setStockMap(map)
          setLoaded(true)
        })
        .catch(() => {
          if (!active) return
          setLoaded(true)
        })

    refresh()
    const interval = setInterval(() => {
      void refresh(true)
    }, 15000)

    const onFocus = () => {
      void refresh(true)
    }
    window.addEventListener('focus', onFocus)

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh(true)
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      active = false
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const inStock = useMemo(
    () => (productId: number) => isProductInStock(productId, stockMap),
    [stockMap],
  )

  return { stockMap, loaded, inStock }
}

